"use client"

import Image from "next/image"
import Link from "next/link"
import { Star, BadgeCheck, Leaf, ExternalLink, ArrowRight, type LucideIcon } from "lucide-react"

import { getDirectusImageUrl, type DirectusProduct } from "@/lib/directus"
import { contentAnalytics, extractAsin, type EventProperties } from "@/lib/analytics"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"

/**
 * "Affiliate CTA — 3 items": a single unified card (serif header + ranked rows +
 * disclosure), ported from the Claude Design project's `Affiliate CTA 3-Item.html`.
 * Each row is rank badge + thumbnail + editorial tag + name + blurb + a "Check
 * price" pill. Deliberately shows NO price — Amazon prices drift, and "Check
 * price" keeps the affiliate link honest without a stale number on the page.
 *
 * Positional editorial tags (Top pick / Best value / Also great) are honest here
 * because the row order is real relevance (see lib/product-keyword-scoring — the
 * season/grass-matched product genuinely sits at #1). The /tools tone renders
 * neither the rank badge nor the tag.
 */

const RANK_TAGS: Array<{ label: string; Icon: LucideIcon }> = [
  { label: "Top pick", Icon: Star },
  { label: "Best value", Icon: BadgeCheck },
  { label: "Also great", Icon: Leaf },
]

interface AffiliatePicksCardProps {
  title: string
  headingLevel?: "h2" | "h3"
  /** Small uppercase kicker above the title. Defaults to "Our picks". */
  eyebrow?: string
  /** Optional one-line context under the title (keeps the specificity hook). */
  description?: string
  products: DirectusProduct[]
  onProductClick?: (product: DirectusProduct) => void
  hideDisclosure?: boolean
  /** Optional "See all" nav link in the header (preserves the old card's
   *  affordance on tool pages). Omit to render header without it. */
  seeAllHref?: string
  onSeeAll?: () => void
  /** Surface id for unified affiliate telemetry — fires `affiliate_product_shown`
   *  on view and `affiliate_product_clicked` on click, both tagged with this, so
   *  CTR is comparable across every product surface. Omit to skip. */
  surface?: string
  /** Extra properties stamped on BOTH the surface impression and its clicks, so
   *  an experiment arm or slot label rides along without a per-surface signature. */
  eventProps?: EventProperties
  /** Value stamped into `data-affiliate-placement` on every anchor AND forwarded
   *  onto the impression and click payloads as `placement`. Callers that own a
   *  legacy `AffiliatePlacement` (the guide cards) pass it so a crawl of the
   *  rendered HTML and a PostHog placement breakdown report the same bucket.
   *  When omitted, `surface` is the attribute value — the rail and tool cards
   *  send no `placement` property, so the surface is their only bucket. */
  placement?: string
  /** Keep the phone (stacked) row layout at every width. For narrow containers
      such as the guide rail, where the `sm:` row layout keys off the viewport
      and overflows the column. */
  stacked?: boolean
  /** Emit one `affiliate_product_shown` per row (`denominator: 'product'`)
   *  alongside the container impression, so per-ASIN CTR is computable on this
   *  surface. Opt-in: the container event is the historical series and stays
   *  the only impression on surfaces that have not enrolled. */
  productImpressions?: boolean
  /** "tool": the /tools section recipe (no eyebrow, rank badges or tags, fluid
   *  H2, elevation card, action buttons). Omit for guides, weeds and the blog rail. */
  tone?: "tool"
}

export function AffiliatePicksCard({
  title,
  headingLevel = "h3",
  eyebrow = "Our picks",
  description,
  products,
  onProductClick,
  hideDisclosure = false,
  seeAllHref,
  onSeeAll,
  surface,
  eventProps,
  placement,
  stacked = false,
  productImpressions = false,
  tone,
}: AffiliatePicksCardProps) {
  const tool = tone === "tool"
  // `placement` rides on the impression too, so the placement bucket that emits
  // clicks also has a denominator (guide-early had clicks and no impressions).
  const placementProps = placement ? { placement } : undefined
  const placementAttr = placement ?? surface
  const impressionRef = useAffiliateImpression(surface, {
    count: products.length,
    // Only where per-product impressions also fire: without the split the two
    // denominators would be indistinguishable rows on one surface.
    ...(productImpressions ? { denominator: 'container' } : {}),
    ...placementProps,
    ...eventProps,
  })
  if (products.length === 0) return null
  const Heading = headingLevel

  return (
    <div
      ref={impressionRef}
      className={tool
        ? "w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-elevation-2"
        : "w-full overflow-hidden rounded-[22px] border border-gray-200 bg-white shadow-[0_16px_44px_-22px_rgba(18,46,30,0.28)]"}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 pt-5 pb-4 sm:px-[26px] sm:pt-[22px] sm:pb-[18px]">
        <div className="min-w-0">
          {eyebrow && !tool && (
            <p className="mb-1.5 text-[11.5px] font-bold uppercase tracking-[0.06em] text-green-700">{eyebrow}</p>
          )}
          <Heading className={tool
            ? "m-0 text-balance text-fluid-h2 font-bold tracking-tight text-gray-900"
            : "m-0 text-balance font-editorial text-[21px] font-semibold leading-[1.2] tracking-[-0.01em] text-gray-900 sm:text-[23px]"}>
            {title}
          </Heading>
          {description && <p className="mt-1.5 text-[13px] leading-snug text-gray-500">{description}</p>}
        </div>
        {seeAllHref && (
          <Link
            href={seeAllHref}
            onClick={onSeeAll}
            className={tool
              ? "inline-flex min-h-11 shrink-0 items-center gap-1 whitespace-nowrap text-sm font-semibold text-green-700 transition-colors hover:text-green-800"
              : "mt-0.5 inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-semibold text-green-700 transition-colors hover:text-green-800"}
          >
            See all
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </Link>
        )}
      </div>

      {/* Ranked rows */}
      <ul className="m-0 list-none p-0">
        {products.map((product, index) => (
          <PicksRow
            key={`${product.id}-${index}`}
            product={product}
            index={index}
            stacked={stacked}
            surface={surface}
            placement={placement}
            eventProps={eventProps}
            productImpressions={productImpressions}
            onProductClick={onProductClick}
            tool={tool}
          />
        ))}
      </ul>

      {/* Footer disclosure */}
      {!hideDisclosure && (
        <div className="bg-[#fafbfa] px-5 py-4 sm:px-[26px]">
          <p className="m-0 text-xs leading-[1.5] text-gray-500">
            As an Amazon Associate we earn from qualifying purchases.
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * One ranked row. Split out of the card so it can own the per-product
 * impression ref: `useAffiliateImpression` must be called once per row, and a
 * hook cannot be called inside a map in the parent.
 */
function PicksRow({
  product,
  index,
  stacked,
  surface,
  placement,
  eventProps,
  productImpressions,
  onProductClick,
  tool,
}: {
  product: DirectusProduct
  index: number
  stacked: boolean
  surface?: string
  placement?: string
  eventProps?: EventProperties
  productImpressions: boolean
  onProductClick?: (product: DirectusProduct) => void
  tool: boolean
}) {
  const asin = extractAsin(product.affiliate_url)
  const placementProps = placement ? { placement } : undefined
  const placementAttr = placement ?? surface
  const productProps: EventProperties = {
    denominator: 'product',
    position: index + 1,
    product_name: product.name,
    affiliate_link: product.affiliate_url,
    ...(asin ? { asin } : {}),
    ...placementProps,
    ...eventProps,
  }
  const rowRef = useAffiliateImpression(
    productImpressions ? surface : undefined,
    productProps,
  )

  const tag = RANK_TAGS[index] ?? RANK_TAGS[RANK_TAGS.length - 1]
  const TagIcon = tag.Icon
  const linked = Boolean(product.affiliate_url)

  return (
            <li
              ref={rowRef}
              className={stacked
                ? "flex list-none flex-col gap-3 border-b border-gray-200 p-4 last:border-b-0"
                : "flex list-none flex-col gap-3 border-b border-gray-200 p-4 last:border-b-0 sm:flex-row sm:items-center sm:gap-5 sm:p-5 sm:px-[26px] sm:py-[22px]"}
            >
              {/* Mobile: rank + thumbnail + text share one row so the copy gets
                  the full remaining width and wraps normally. `sm:contents`
                  flattens this wrapper on desktop so rank/thumb/body/CTA become
                  direct flex children of the row again (original layout). */}
              <div className={stacked ? "flex flex-wrap items-start gap-3" : "flex items-start gap-3 sm:contents"}>
                {!tool && (
                  <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg border border-green-200 bg-green-50 text-[13px] font-extrabold text-green-700">
                    {index + 1}
                  </span>
                )}

                {/* Thumbnail — compact square on mobile, larger on desktop */}
                <div className={stacked
                    ? "flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-[14px] border border-gray-100 bg-[#f3f6f3]"
                    : "flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-[14px] border border-gray-100 bg-[#f3f6f3] sm:h-24 sm:w-24"}>
                  <Image
                    src={getDirectusImageUrl(product.image, 192, 192)}
                    alt={product.name}
                    width={192}
                    height={192}
                    loading="lazy"
                    className="h-full w-full object-contain p-2"
                  />
                </div>

                {/* Body — title + description wrap in full, never truncated */}
                <div className={stacked ? "flex min-w-0 basis-full flex-col gap-[5px]" : "flex min-w-0 flex-1 flex-col gap-[5px]"}>
                  {!tool && (
                    <span className="inline-flex items-center gap-1.5 self-start rounded-full border border-green-200 bg-green-50 px-2.5 py-[3px] text-[10.5px] font-bold uppercase tracking-[0.05em] text-green-700">
                      <TagIcon className="h-[11px] w-[11px]" aria-hidden="true" />
                      {tag.label}
                    </span>
                  )}
                  <h4 className="mt-0.5 text-[15px] font-bold leading-[1.3] text-gray-900 sm:text-base">{product.name}</h4>
                  {product.description && (
                    <p className="text-[13px] leading-[1.5] text-gray-500 sm:text-[13.5px]">{product.description}</p>
                  )}
                </div>
              </div>

              {/* CTA — full-width tap target on mobile (its own line below the
                  content), auto-width right-aligned button on desktop. */}
              {linked && (
                <a
                  href={product.affiliate_url}
                  target="_blank"
                  rel="noopener noreferrer sponsored nofollow"
                  data-affiliate="true"
                  data-affiliate-placement={placementAttr}
                  onClick={() => {
                    if (surface) {
                      contentAnalytics.affiliateProductClicked(surface, {
                        productName: product.name,
                        affiliateLink: product.affiliate_url,
                        position: index + 1,
                        ...placementProps,
                        ...eventProps,
                      })
                    }
                    onProductClick?.(product)
                  }}
                  className={tool
                    ? "press inline-flex min-h-11 w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-action px-5 text-sm font-bold text-action-foreground hover:bg-action-hover hover:text-white sm:w-auto"
                    : stacked
                    ? "inline-flex min-h-[44px] w-full shrink-0 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-green-600 to-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-[0_10px_22px_-10px_rgba(22,163,74,0.5)] transition hover:-translate-y-0.5 hover:text-white"
                    : "inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-green-600 to-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-[0_10px_22px_-10px_rgba(22,163,74,0.5)] transition hover:-translate-y-0.5 hover:text-white sm:w-auto sm:min-h-[46px] sm:py-[11px] sm:text-[14.5px]"}
                >
                  Check price
                  <ExternalLink className="h-[15px] w-[15px]" aria-hidden="true" />
                </a>
              )}
            </li>
  )
}
